import React, { createContext, useContext, useState, useEffect } from "react";
import { OktaAuth } from "@okta/okta-auth-js";
import oktaConfig from "../utils/oktaConfig";

export const oktaAuth = new OktaAuth(oktaConfig);

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Always run Okta's authentication check to initialize all components
        const isAuthenticated = await oktaAuth.isAuthenticated();
        // Normal production flow
        if (isAuthenticated) {
          const user = await oktaAuth.getUser();

          const accessToken = oktaAuth.getAccessToken();

          // Try to get email from multiple sources
          let userEmail = null;

          // First try: get email from user object
          if (user && user.email) {
            userEmail = user.email;
          }

          // Second try: get email from idToken claims
          if (!userEmail) {
            try {
              const idToken = oktaAuth.getIdToken();
              if (idToken) {
                const idTokenParts = idToken.split(".");
                if (idTokenParts.length === 3) {
                  const idTokenPayload = JSON.parse(atob(idTokenParts[1]));
                  if (idTokenPayload.email) {
                    userEmail = idTokenPayload.email;
                  }
                }
              }
            } catch (e) {
              console.error("Error decoding idToken:", e);
            }
          }

          // Third try: get email from accessToken
          if (!userEmail) {
            const tokenParts = accessToken.split(".");
            if (tokenParts.length === 3) {
              try {
                const payload = JSON.parse(atob(tokenParts[1]));
                if (payload.sub && payload.sub.includes("@")) {
                  userEmail = payload.sub; // Sometimes email is in 'sub' field
                } else if (payload.unique_name) {
                  userEmail = payload.unique_name;
                }
              } catch (e) {
                console.error("Error decoding accessToken:", e);
              }
            }
          }

          if (userEmail) {
            localStorage.setItem("userEmail", userEmail);

            // Extract domain from email and set domain cookie
            const emailDomain = userEmail.split("@")[1];
            if (emailDomain) {
              const domain = emailDomain.split(".")[0]; // e.g., "savills" from "savills.us"
              localStorage.setItem("container", domain); // Store container for table downloads
            }
          } else {
            console.error("Could not extract email from any token source");
          }

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
          });
        }
      } catch (error) {
        console.error("Authentication error:", error);
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error,
        });
      }
    };

    checkAuthentication();

    // Updated subscription pattern compatible with newer Okta Auth JS versions
    let unlisten = null;
    try {
      // This is the modern way to subscribe to auth state changes
      unlisten = oktaAuth.authStateManager.subscribe(checkAuthentication);
    } catch (e) {
      console.error("Error subscribing to auth state:", e);
    }

    return () => {
      // Safe cleanup that works regardless of return type
      if (unlisten) {
        if (typeof unlisten === "function") {
          unlisten();
        } else if (
          unlisten.unsubscribe &&
          typeof unlisten.unsubscribe === "function"
        ) {
          unlisten.unsubscribe();
        }
      }
    };
  }, []);

  async function login() {
    await oktaAuth.signInWithRedirect();
  }

  async function logout() {
    await oktaAuth.signOut();
  }

  const value = {
    authState,
    login,
    logout,
    oktaAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
